import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config/apiConfig';

const EmailSettings = () => {
  const [form, setForm] = useState({
    smtp_host: '',
    smtp_port: '',
    smtp_user: '',
    smtp_pass: '',
    recipient_email: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/email-config`)
      .then(res => res.json())
      .then(data => {
        if (data.success && data.config) {
          setForm(data.config);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_BASE_URL}/email-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ type: 'success', message: 'Settings saved!' });
      } else {
        setStatus({ type: 'error', message: data.error || 'Failed to save.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div className="bg-gray-800 rounded-lg p-6 max-w-xl mx-auto">
      <h2 className="text-2xl font-bold mb-4 text-white">Email Settings</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-gray-300 mb-1">SMTP Host</label>
          <input type="text" name="smtp_host" value={form.smtp_host} onChange={handleChange} className="w-full p-2 rounded bg-gray-700 text-white" required />
        </div>
        <div>
          <label className="block text-gray-300 mb-1">SMTP Port</label>
          <input type="number" name="smtp_port" value={form.smtp_port} onChange={handleChange} className="w-full p-2 rounded bg-gray-700 text-white" required />
        </div>
        <div>
          <label className="block text-gray-300 mb-1">SMTP User</label>
          <input type="text" name="smtp_user" value={form.smtp_user} onChange={handleChange} className="w-full p-2 rounded bg-gray-700 text-white" required />
        </div>
        <div>
          <label className="block text-gray-300 mb-1">SMTP Password</label>
          <input type="password" name="smtp_pass" value={form.smtp_pass} onChange={handleChange} className="w-full p-2 rounded bg-gray-700 text-white" required />
        </div>
        <div>
          <label className="block text-gray-300 mb-1">Recipient Email</label>
          <input type="email" name="recipient_email" value={form.recipient_email} onChange={handleChange} className="w-full p-2 rounded bg-gray-700 text-white" required />
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Settings'}</button>
        {status && <div className={`mt-2 text-${status.type === 'success' ? 'green' : 'red'}-400`}>{status.message}</div>}
      </form>
      <ServiceEditor />
    </div>
  );
};

const ServiceEditor = () => {
  const [services, setServices] = useState([]);
  const [newService, setNewService] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE_URL}/messages/services`)
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.services)) {
          setServices(data.services);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleAdd = () => {
    if (newService.trim() && !services.includes(newService.trim())) {
      setServices([...services, newService.trim()]);
      setNewService('');
    }
  };

  const handleRemove = idx => {
    setServices(services.filter((_, i) => i !== idx));
  };

  const handleChange = (idx, value) => {
    setServices(services.map((s, i) => (i === idx ? value : s)));
  };

  const handleSave = async () => {
    setSaving(true);
    setStatus(null);
    try {
      const res = await fetch(`${API_BASE_URL}/messages/services`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ services })
      });
      const data = await res.json();
      if (data.success) {
        setStatus({ type: 'success', message: 'Services updated!' });
      } else {
        setStatus({ type: 'error', message: data.error || 'Failed to save.' });
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div>Loading services...</div>;

  return (
    <div className="mt-8 bg-gray-700 rounded-lg p-4">
      <h3 className="text-xl font-bold mb-2 text-white">Edit Services</h3>
      <div className="space-y-2">
        {services.map((service, idx) => (
          <div key={idx} className="flex gap-2 items-center">
            <input
              type="text"
              value={service}
              onChange={e => handleChange(idx, e.target.value)}
              className="w-full p-2 rounded bg-gray-800 text-white"
            />
            <button type="button" onClick={() => handleRemove(idx)} className="btn btn-danger">Remove</button>
          </div>
        ))}
        <div className="flex gap-2 mt-2">
          <input
            type="text"
            value={newService}
            onChange={e => setNewService(e.target.value)}
            className="w-full p-2 rounded bg-gray-800 text-white"
            placeholder="Add new service"
          />
          <button type="button" onClick={handleAdd} className="btn btn-primary">Add</button>
        </div>
        <button type="button" onClick={handleSave} className="btn btn-primary mt-4" disabled={saving}>{saving ? 'Saving...' : 'Save Services'}</button>
        {status && <div className={`mt-2 text-${status.type === 'success' ? 'green' : 'red'}-400`}>{status.message}</div>}
      </div>
    </div>
  );
};

export default EmailSettings; 